import React, { useEffect, useState } from "react";
import { useApp } from "../../context/AppContext";
import { Provider, TherapistProfileData } from "../../types";
import defaultAvatar from "../../assets/avatar.png";
import { API_ENDPOINTS } from "../../config";
import { PAYMENT_CONSTANTS } from "../../constants/payment";
import PaymentModal from "./PaymentModal";

interface ProviderProfileProps {
  provider: Provider;
  onBack: () => void;
  conversationId?: string;
  // Optional: when provided, render using provider JSON instead of fetching server user info
  providerProfile?: ProviderProfileEntry;
}

interface ProviderDetailInfo {
  id: string;
  type: string;
  email: string;
  name: string;
}

// Provider JSON entry schema from /providers/*.json
interface ProviderProfileEntry {
  name: string;
  title?: string;
  location?: string;
  specialties?: {
    "Top Specialties"?: string[];
    Expertise?: string[];
  };
  phone?: string;
  self_description?: string;
  treatment_approach?: string[];
  languages?: string[];
  insurance_accepted?: string[];
  session_fee?: Record<string, string>;
  photo_urls?: string[];
  years_practicing?: string | number | null;
  education?: string[];
  profile_url?: string;
  created_at?: string;
  locations?: TherapistProfileData['locations'];
  licenses?: TherapistProfileData['licenses'];
}

const ProviderProfile: React.FC<ProviderProfileProps> = ({
  provider,
  onBack,
  conversationId = "",
  providerProfile,
}) => {
  const { initiateCall, state } = useApp();
  const [providerInfo, setProviderInfo] = useState<ProviderDetailInfo | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Check payment status
  const hasPaid = state.user?.has_paid || false;

  useEffect(() => {
    // Check if we have structured profile data (for unregistered therapists from DB/JSON)
    const hasStructuredProfileData = provider.profile_data &&
      (provider.profile_data.specialties || provider.profile_data.treatment_approach ||
       provider.profile_data.education || provider.profile_data.self_description);

    // If we have structured profile_data, use it directly (for DB therapists)
    if (hasStructuredProfileData) {
      setLoading(false);
      return;
    }

    // If we have a provider profile (from JSON), use it
    if (providerProfile) {
      setLoading(false);
      return;
    }

    // Otherwise, fetch from server (for registered users)
    const fetchProviderInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${API_ENDPOINTS.LINSTER_INFO}?user_id=${provider.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch provider info");
        }

        const data = await response.json();
        setProviderInfo(data);
      } catch (error) {
        console.error("Error fetching provider info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProviderInfo();
  }, [provider.id, provider.profile_data, providerProfile]);

  const handleCall = async () => {
    try {
      await initiateCall(provider.id, conversationId);
    } catch (error) {
      console.error("Failed to initiate call:", error);
    }
  };


  if (loading) {
    return <div className="provider-profile">Loading...</div>;
  }

  // Show payment gate if user hasn't paid
  if (!hasPaid) {
    // Get profile photo from profile_data if available (same logic as ProvidersList)
    const profilePhoto = provider.profile_data?.photo_urls?.[0] || provider.avatar || defaultAvatar;
    const displayTitle = provider.profile_data?.title || provider.title || provider.type;
    const firstName = provider.name.split(" ")[0] || provider.name;

    return (
      <>
        <div className="provider-profile">
          <div className="provider-profile-header">
            <button className="back-button" onClick={onBack}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <h2>Provider Details</h2>
          </div>

          <div className="provider-profile-content">
            <div className="provider-profile-main">
              <div className="provider-profile-avatar">
                <img
                  src={profilePhoto}
                  alt="Therapist"
                />
              </div>
              <h3 className="provider-profile-name">
                {firstName} {/* First name only */}
              </h3>
              <p className="provider-profile-type">{displayTitle}</p>
              <div style={{
                background: "#fef3c7",
                padding: "1rem",
                borderRadius: "8px",
                marginTop: "1rem",
                textAlign: "center"
              }}>
                <p style={{ margin: 0, color: "#92400e", fontSize: "0.95rem" }}>
                  🔒 Unlock full profile and contact details for ${PAYMENT_CONSTANTS.PRICE_DOLLARS.toFixed(2)}
                </p>
              </div>
            </div>

            <button
              className="payment-unlock-button"
              onClick={() => setShowPaymentModal(true)}
              style={{
                width: "100%",
                padding: "1rem",
                fontSize: "1.05rem",
                fontWeight: "600",
                color: "white",
                background: "linear-gradient(90deg, #7B9FEC 0%, #D896C8 100%)",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                marginTop: "2rem",
              }}
            >
              View Full Profile
            </button>
          </div>
        </div>
        {showPaymentModal && (
          <PaymentModal
            onClose={() => setShowPaymentModal(false)}
          />
        )}
      </>
    );
  }

  // Use profile_data from recommendation if it has structured therapist fields
  // For registered users, profile_data might only have basic fields, so check for specialties/treatment_approach
  const hasStructuredProfile = provider.profile_data &&
    (provider.profile_data.specialties || provider.profile_data.treatment_approach ||
     provider.profile_data.education || provider.profile_data.self_description);

  const profileData = (hasStructuredProfile ? provider.profile_data as ProviderProfileEntry : null) || providerProfile;

  return (
    <div className="provider-profile">
      <div className="provider-profile-header">
        <button className="back-button" onClick={onBack}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h2>Provider Details</h2>
      </div>

      <div className="provider-profile-content">
        {profileData ? (
          <>
            <div className="provider-profile-main">
              <div className="provider-profile-avatar">
                <img
                  src={profileData?.photo_urls?.[0] || defaultAvatar}
                  alt={profileData?.name || "Profile"}
                />
              </div>
              <h3 className="provider-profile-name">{profileData.name}</h3>
              {profileData.title && (
                <p className="provider-profile-type">{profileData.title}</p>
              )}
              {profileData.location && (
                <p className="provider-profile-email">{profileData.location}</p>
              )}
              {profileData.phone && (
                <p className="provider-profile-email">{profileData.phone}</p>
              )}
              {profileData.profile_url && (
                <p className="provider-profile-email">
                  <a href={profileData.profile_url} target="_blank" rel="noreferrer">
                    View Full Profile
                  </a>
                </p>
              )}
              {profileData.created_at && (
                <p className="provider-profile-email">
                  Member Since: {new Date(profileData.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </p>
              )}
            </div>

            {profileData.self_description && (
              <div className="professional-section">
                <h3>About</h3>
                <p>{profileData.self_description}</p>
              </div>
            )}

            {profileData.years_practicing && (
              <div className="professional-section">
                <h3>Experience</h3>
                <p>{profileData.years_practicing} years in practice</p>
              </div>
            )}

            <div className="professional-section">
              <h3>Specialties & Expertise</h3>

              {(() => {
                // Handle both array format (from registered therapists) and object format (from JSON providers)
                if (Array.isArray(profileData.specialties)) {
                  // Array format: just render the flat list
                  return (
                    <div className="specialty-list">
                      {profileData.specialties.map((s, i) => (
                        <span key={i} className="specialty-tag">
                          {s}
                        </span>
                      ))}
                    </div>
                  );
                }

                // Object format with Top Specialties / Expertise
                const hasTopSpecialties = (profileData.specialties?.["Top Specialties"]?.length ?? 0) > 0;
                const hasExpertise = (profileData.specialties?.Expertise?.length ?? 0) > 0;

                // If has Top Specialties, show both sections with labels
                if (hasTopSpecialties && profileData.specialties) {
                  return (
                    <>
                      <div className="specialty-subsection">
                        <h4>Top Specialties</h4>
                        <div className="specialty-list">
                          {profileData.specialties["Top Specialties"]!.map((s, i) => (
                            <span key={`top-${i}`} className="specialty-tag">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>

                      {hasExpertise && (
                        <div className="specialty-subsection">
                          <h4>Additional Expertise</h4>
                          <div className="specialty-list">
                            {profileData.specialties.Expertise!.map((s, i) => (
                              <span key={`exp-${i}`} className="specialty-tag">
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                }

                // If no Top Specialties but has Expertise, show Expertise without subsection label
                if (hasExpertise && profileData.specialties) {
                  return (
                    <div className="specialty-list">
                      {profileData.specialties.Expertise!.map((s, i) => (
                        <span key={`exp-${i}`} className="specialty-tag">
                          {s}
                        </span>
                      ))}
                    </div>
                  );
                }

                return null;
              })()}
            </div>

            <div className="professional-section">
              <h3>Treatment Methods</h3>
              <div className="method-list">
                {(profileData.treatment_approach || []).map((m, i) => (
                  <span key={i} className="method-tag">
                    {m}
                  </span>
                ))}
              </div>
            </div>

            {(profileData.education && profileData.education.length > 0) && (
              <div className="professional-section">
                <h3>Education</h3>
                <div className="education-list">
                  {profileData.education.map((e, i) => {
                    // Handle both string format and object format
                    if (typeof e === 'string') {
                      return (
                        <div key={i} className="education-item">
                          <span className="degree">{e}</span>
                        </div>
                      );
                    } else if (typeof e === 'object' && e !== null) {
                      // Object format with degree, field_of_study, graduation_year
                      const eduObj = e as any;
                      const parts = [];
                      if (eduObj.degree) parts.push(String(eduObj.degree));
                      if (eduObj.field_of_study) parts.push(String(eduObj.field_of_study));
                      if (eduObj.graduation_year) parts.push(String(eduObj.graduation_year));
                      const eduText = parts.join(', ') || 'Education details';
                      return (
                        <div key={i} className="education-item">
                          <span className="degree">{eduText}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            )}

            <div className="professional-section">
              <h3>Languages</h3>
              <div className="method-list">
                {(profileData.languages || []).map((l, i) => (
                  <span key={i} className="method-tag">
                    {l}
                  </span>
                ))}
              </div>
            </div>

            {(profileData.session_fee &&
              Object.keys(profileData.session_fee).length > 0) && (
              <div className="professional-section">
                <h3>Fees</h3>
                <div className="education-list">
                  {Object.entries(profileData.session_fee).map(
                    ([k, v]) => (
                      <div key={k} className="education-item">
                        <span className="degree">{k}: {v}</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {(profileData.insurance_accepted &&
              profileData.insurance_accepted.length > 0) && (
              <div className="professional-section">
                <h3>Insurance Accepted</h3>
                <div className="method-list">
                  {profileData.insurance_accepted.map((ins, i) => (
                    <span key={i} className="method-tag">{ins}</span>
                  ))}
                </div>
              </div>
            )}

            {(profileData.locations && profileData.locations.length > 0) && (
              <div className="professional-section">
                <h3>Office Locations</h3>
                <div className="education-list">
                  {profileData.locations.map((loc, i) => {
                    const parts = [];
                    if (loc.city) parts.push(loc.city);
                    if (loc.state) parts.push(loc.state);
                    if (loc.postal_code) parts.push(loc.postal_code);
                    const locationText = parts.join(', ') || 'Location details not available';
                    const inPersonText = loc.in_person ? 'In-person' : 'Telehealth only';
                    const isPrimaryText = loc.is_primary ? ' (Primary)' : '';
                    return (
                      <div key={i} className="education-item">
                        <span className="degree">{locationText} - {inPersonText}{isPrimaryText}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {(profileData.licenses && profileData.licenses.length > 0) && (
              <div className="professional-section">
                <h3>Licensed States</h3>
                <div className="method-list">
                  {profileData.licenses.map((lic, i) => {
                    const licenseText = lic.license_type ? `${lic.state} (${lic.license_type})` : lic.state;
                    return (
                      <span key={i} className="method-tag">{licenseText}</span>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        ) : (
          // Simple fallback for providers without complete profile
          <>
            <div className="provider-profile-main">
              <div className="provider-profile-avatar">
                <img src={defaultAvatar} alt={provider.name} />
              </div>
              <h3 className="provider-profile-name">{provider.name}</h3>
              <p className="provider-profile-type">{provider.type || 'Therapist'}</p>
              {provider.email && <p className="provider-profile-email">{provider.email}</p>}
            </div>
            <div className="professional-section">
              <p>Profile information not yet available.</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderProfile;
